import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Autocomplete,
    TextField,
    Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CustomTable from "../../../components/Table/Table";
import { useProjectActivityViewModel } from "../ProjectActivity/ProjectActivityViewModel";
import { useTagApi } from "../../../hooks/useTagApi";
import { useActivityApi } from "../../../hooks/useActivityApi"; // Added for fetching activities
import { useMilestoneActivityApi } from "../../../hooks/useProjectMilestoneActivity";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Snackbar from "@mui/material/Snackbar";
import { filter } from "rxjs";
import MuiAlert from "@mui/material/Alert";
// This styled wrapper is a safer way to hide the table header than global CSS injection.
// Ideally, the CustomTable component should accept a `showHeader={false}` prop.
const TableWrapper = styled(Box)({
    "& .MuiTableHead-root": {
        display: "none",
    },
});

// A utility function to format dates consistently and reduce repetition.
const formatDate = (value, prefix) => {
    if (!value) return "";
    try {
        const dateObj =
            typeof value === "number" || /^\d{13,}$/.test(value)
                ? new Date(parseInt(value, 10))
                : new Date(value);
        if (isNaN(dateObj.getTime())) return `${prefix}: Invalid Date`;
        return `${prefix}: ${dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        })}`;
    } catch (e) {
        return `${prefix}: Invalid Date`;
    }
};

const MilestoneActivitySelectionPage = ({ projectId }) => {
    const [expandedRows, setExpandedRows] = useState({});
    const [tagOptions, setTagOptions] = useState([]);
    const [rowTagSelections, setRowTagSelections] = useState({});
    const [milestoneActivities, setMilestoneActivities] = useState({});
    const [selectedActivityOptionsByMilestone, setSelectedActivityOptionsByMilestone] = useState({});
    const [initialSelectedActivityOptionsByMilestone, setInitialSelectedActivityOptionsByMilestone] = useState({});
    const [savingMilestone, setSavingMilestone] = useState(null);
    const [expandAllLoading, setExpandAllLoading] = useState(false);
    const [editModeByMilestone, setEditModeByMilestone] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const [allActivities, setAllActivities] = useState([]);

    const { tasks, projectDetails } = useProjectActivityViewModel(projectId);
    const { getTagsByType } = useTagApi();
    const { getActivitiesByTagType } = useActivityApi();
    const { getMilestoneActivitiesByIds, createMilestoneActivity } = useMilestoneActivityApi();

    const filteredTasks = useMemo(
        () => tasks.filter(task => !String(task.task_id).startsWith("temp-")),
        [tasks]
    );

    const tagType = useMemo(() => {
        if (!projectDetails?.project_type) return null;
        if (projectDetails.project_type === "SUPPORT") return "SUPPORT";
        if (projectDetails.project_type === "INTERNAL") return "INTERNAL";
        if (["BILLABLE", "NON_BILLABLE"].includes(projectDetails.project_type)) return "BUSINESS";
        return null;
    }, [projectDetails?.project_type]);

    // Memoize API functions so they don't change on every render
    const stableGetTagsByType = useCallback(getTagsByType, []);
    const stableGetActivitiesByTagType = useCallback(getActivitiesByTagType, []);
    const stableGetMilestoneActivitiesByIds = useCallback(getMilestoneActivitiesByIds, []);

    // Fetch tags only once per project type
    useEffect(() => {
        async function fetchTags() {
            if (!tagType) return;
            const result = await stableGetTagsByType(tagType);
            setTagOptions(
                result?.success
                    ? result.data.map(tag => ({ label: tag.tag_name, value: tag.tag_id }))
                    : []
            );
        }
        fetchTags();
    }, [tagType, stableGetTagsByType]);

    // Fetch activities once per project type
    
    useEffect(() => {
        async function fetchActivities() {
            if (!tagType) return;
            const result = await stableGetActivitiesByTagType(tagType);
            setAllActivities(result?.success ? result.data : []);
        }
        fetchActivities();
    }, [tagType, stableGetActivitiesByTagType]);
    useEffect(() => {
        async function fetchMilestoneActivities() {
            if (!filteredTasks.length) return;
            const milestoneIds = filteredTasks.map(t => t.milestone_id || t.task_id);
            const res = await stableGetMilestoneActivitiesByIds(milestoneIds);
            if (res.success && Array.isArray(res.data)) {
                const mapping = {};
                res.data.forEach(row => {
                    mapping[row.milestone_id] = (row.activities || []).map(act => ({
                        label: act.activity_name,
                        value: act.activity_id,
                        remove_flag: act.remove_flag
                    }));
                });
                setMilestoneActivities(mapping);
                setSelectedActivityOptionsByMilestone(mapping);
                setInitialSelectedActivityOptionsByMilestone(mapping);
            }
        }
        fetchMilestoneActivities();
    }, [filteredTasks, stableGetMilestoneActivitiesByIds]);

    const handleRowExpand = (row, isExpanded) => {
        const milestoneId = row.task_id;
        setExpandedRows(prev => ({ ...prev, [milestoneId]: isExpanded }));
    };

    const handleExpandRow = expand => {
        setExpandAllLoading(true);
        const newExpandedRows = {};
        filteredTasks.forEach(row => {
            newExpandedRows[row.task_id] = expand;
        });
        setExpandedRows(newExpandedRows);
        setExpandAllLoading(false);
    };


    const handleTagChange = (milestone_id, newValues) => {
        setRowTagSelections((prev) => ({ ...prev, [milestone_id]: newValues }));
    };
    const isUnsavedChip = (milestoneId, activity) => {
        const saved = initialSelectedActivityOptionsByMilestone[milestoneId] || [];
        return !saved.some(a => a.value === activity.value);
    };

    const handleSaveChanges = async (milestoneId) => {
        setSavingMilestone(milestoneId);

        const activitiesToLink = (selectedActivityOptionsByMilestone[milestoneId] || []).map(opt => opt.value);
        if(activitiesToLink.length === 0){
            setSnackbarMsg("Please add at least one activity before saving.");
            setSnackbarType("warning");
            setSnackbarOpen(true);
            setSavingMilestone(null);
            return;
        }
        const result = await createMilestoneActivity(milestoneId, activitiesToLink);

        if (result.success) {
            // Save snapshot to the "current truth"
            setInitialSelectedActivityOptionsByMilestone(prev => ({
                ...prev,
                [milestoneId]: selectedActivityOptionsByMilestone[milestoneId] || []
            }));

            // Clear selected tag IDs for this milestone
            setRowTagSelections(prev => ({ ...prev, [milestoneId]: [] }));

            setSnackbarMsg("Milestone activities updated successfully!");
            setSnackbarType("success");
            setSnackbarOpen(true);
            setEditModeByMilestone(prev => ({ ...prev, [milestoneId]: false }));
        } else {
            setSnackbarMsg("Failed to update milestone activities!");
            setSnackbarType("error")
            setSnackbarOpen(true);
        }

        setSavingMilestone(null);
    };


    const handleCancelChanges = (milestoneId) => {
        setSelectedActivityOptionsByMilestone(prev => ({
            ...prev,
            [milestoneId]: initialSelectedActivityOptionsByMilestone[milestoneId] || []
        }));

        setEditModeByMilestone(prev => ({
            ...prev,
            [milestoneId]: false,
        }));
    };

    const getFilteredActivities = useCallback(
        (milestoneId, selectedTags, activities) => {
          
            let filtered = allActivities;
           
            // If tags selected, filter by tags
            if (selectedTags.length > 0) {
                filtered = filtered.filter(act => {
                    const actTagIds = act.tags?.map(t => Number(t.tag_id)) || [];
                    return selectedTags.some(tagId => actTagIds.includes(Number(tagId)));
                });
            }
            const mappedIds = (activities || []).map(a => String(a.activity_id));
            const selectedForMilestone =
                selectedActivityOptionsByMilestone[milestoneId]?.map(opt => String(opt.value)) || [];
            return filtered
                .filter(
                    act =>
                        !mappedIds.includes(String(act.activity_id)) &&
                        !selectedForMilestone.includes(String(act.activity_id))
                )
                .map(act => ({ label: act.activity_name, value: act.activity_id }));
        },
        [allActivities, selectedActivityOptionsByMilestone]
    );

    const columns = [
        { id: "milestone_code", label: "Milestone Code", width: 120 },
        {
            id: "task_name",
            label: "Milestone Name",
            width: 180,
            render: (value) => (
                <Tooltip title={value || ""} arrow placement="top">
                    <Box
                        sx={{
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                        }}
                    >
                        {value}
                    </Box>
                </Tooltip>
            ),
            sx: {
                width: "180px",
                minWidth: "180px",
                maxWidth: "180px",
            },
        },
        { id: "start_date", label: "Start Date", render: (value) => formatDate(value, "Start") },
        { id: "end_date", label: "End Date", render: (value) => formatDate(value, "End") },
    ];

    useEffect(() => {
        // Enable edit mode by default only for milestones with no activities
        const initialEditMode = {};
        filteredTasks.forEach(row => {
            const milestoneId = row.task_id;
            const hasActivities = (milestoneActivities[milestoneId] || []).length > 0;
            initialEditMode[milestoneId] = !hasActivities;
        });
        setEditModeByMilestone(initialEditMode);
    }, [filteredTasks, milestoneActivities]);

    const expandedRowRender = row => {
        const milestoneId = row.task_id;
        const activities = milestoneActivities[milestoneId] || [];
        const selectedTags = rowTagSelections[milestoneId] || [];
        const isSaving = savingMilestone === milestoneId;
        const isEditMode = editModeByMilestone[milestoneId];
        const filteredActivityOptions = getFilteredActivities(milestoneId, selectedTags, activities);
        return (
            <Box display="flex" flexDirection="column" gap={2} mb={2} mt={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    <Box display="flex" flexWrap="wrap" gap={1} flex={1}>
                        {(selectedActivityOptionsByMilestone[milestoneId] || []).length === 0 ? (
                            <Typography variant="body2">No activities for this milestone.</Typography>
                        ) : (
                            selectedActivityOptionsByMilestone[milestoneId]?.map(act => {
                                const unsaved = isUnsavedChip(milestoneId, act);
                                return (
                                    <Chip
                                        key={act.value}
                                        label={act.label}
                                        onDelete={isEditMode && !act.remove_flag ? () => {
                                            setSelectedActivityOptionsByMilestone(prev => ({
                                                ...prev,
                                                [milestoneId]: (prev[milestoneId] || []).filter(a => a.value !== act.value)
                                            }));
                                        } : undefined}
                                        size="small"
                                        sx={{
                                            backgroundColor: unsaved ? "#f2f2f2" : "#c5d0f9", // unsaved yellowish, saved bluish
                                            '& .MuiChip-deleteIcon': {
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                '&:hover': {
                                                    color: 'rgba(0, 0, 0, 0.8)',
                                                }
                                            }
                                        }}
                                    />
                                )
                            })
                        )}
                    </Box>

                    {/* Buttons */}
                    <Box display="flex" gap={1}>
                        {isEditMode && row.task_status_code !== "COMPLETED" ? (
                            <>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleSaveChanges(milestoneId)}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleCancelChanges(milestoneId)}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                <Tooltip title={row.task_status_code === "COMPLETED" ? "Milestone is completed" : ""} arrow placement="top">
                                    <span>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => setEditModeByMilestone(prev => ({ ...prev, [milestoneId]: true }))}
                                            disabled={row.task_status_code === "COMPLETED"}
                                        >
                                            Edit
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        setExpandedRows(exp => ({ ...exp, [milestoneId]: false }))
                                    }
                                >
                                    Hide
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Dropdowns */}
                <Box display="flex" gap={4} alignItems="center" width="100%">
                    {/* Tags */}
                    <Box display="flex" alignItems="center" gap={1} flex={0.5}>
                        <Typography variant="subtitle4" fontWeight="600" sx={{ whiteSpace: "nowrap" }}>
                            Tags
                        </Typography>
                        <Box flex={1}>
                            <Dropdown
                                multiple
                                options={tagOptions}
                                value={selectedTags}
                                onChange={(newValues) => handleTagChange(milestoneId, newValues)}
                                placeholder="Select Tags"
                                fullWidth
                                disabled={!isEditMode || isSaving}
                            />
                        </Box>
                    </Box>

                    {/* Search Activities */}
                    <Box display="flex" alignItems="center" gap={1} flex={1.5}>
                        <Typography variant="subtitle4" fontWeight="600" sx={{ whiteSpace: "nowrap" }}>
                            Search Activities
                        </Typography>
                        <Box flex={1}>
                            <Dropdown
                                placeholder="Search Activities"
                                multiple
                                options={filteredActivityOptions}
                                value={[]}
                                onChange={newValues => {
                                    const newOptionObjects = newValues
                                        .map(opt =>
                                            typeof opt === "object" && opt.label
                                                ? opt
                                                : allActivities.find(a => String(a.activity_id) === String(opt))
                                        )
                                        .filter(Boolean)
                                        .map(act => ({ label: act.activity_name, value: act.activity_id }));
                                    setSelectedActivityOptionsByMilestone(prev => ({
                                        ...prev,
                                        [milestoneId]: [...(prev[milestoneId] || []), ...newOptionObjects],
                                    }));
                                }}
                                fullWidth
                                disabled={!isEditMode || isSaving}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };



    const rowSx = (row) => {
       
        const isExpanded = expandedRows.includes(row.id);
        return {
            backgroundColor: isExpanded ? "#2f3ba2" : "inherit",
            color: isExpanded ? "white" : "inherit",
            "& .MuiTableCell-root": {
                color: isExpanded ? "white" : "inherit",
            },
        };
    };

    return (
        <>
            <TableWrapper>
                <CustomTable
                    columns={columns}
                    data={filteredTasks}
                    showActions={false}
                    showAddButton={false}
                    showSearch={true}
                    stickyHeader={true}
                    showExpandableRows={true}
                    expandedRowRender={expandedRowRender}
                    onRowExpand={handleRowExpand}
                    expandedRows={expandedRows}
                    onExpandAll={handleExpandRow}
                    expandAllLoading={expandAllLoading}
                    rowSx={rowSx}
                    title="Assign Activities to Milestones"
                    subtitle="Expand a milestone to add or remove its activities."
                    enableRowClickExpand= {true}
                />
            </TableWrapper>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarType}
                    sx={{ width: "100%" }}
                >
                    {snackbarMsg}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default MilestoneActivitySelectionPage;